import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Icon,
  Link,
  useDisclosure,
} from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';
import Loader from './Loader';
import ControlPanel from './ControlPanel';
import { Download01 } from '@kula-ai/icons/dual-tone/Download01';
import { useShowToast } from '@/hooks';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { IconButton } from '@/components/ats';
import { FullScreenModal } from '@/componentsV1/FullScreenModal';
import { Maximize01 } from '@kula-ai/icons/outline/Maximize01';
import { KText } from '@/ui-library/components/text';
import { Replace } from '@/assets/icons/ats';
import { useKFlags } from '@/hooks/use-k-flags';
import { PdfViewer } from '../react-pdf-dev';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface Props {
  file: any;
  hideControl?: boolean;
  containerStyles?: FlexProps;
}

export const PDFReader: React.FC<Props> = ({
  file,
  hideControl = false,
  containerStyles = {},
}) => {
  const [scale, setScale] = useState(1.0);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  useEffect(() => {
    const container = pdfContainerRef.current;
    if (container) {
      const pageElement = container.querySelector(
        `.react-pdf__Page[data-page-number="${pageNumber}"]`
      );
      if (pageElement) {
        container.scrollTo({
          top: (pageElement as HTMLElement).offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }, [pageNumber]);

  return (
    <Box w="100%" h="100%" overflow="hidden">
      <Loader isLoading={isLoading} />
      <Flex
        h="100%"
        overflow="hidden"
        direction="column"
        id="pdf-container"
        border="1px solid"
        borderTopRadius="sm"
        borderColor="border-color"
        {...containerStyles}
      >
        {!hideControl && (
          <ControlPanel
            file={file}
            pageNumber={pageNumber}
            numPages={numPages}
            setPageNumber={setPageNumber}
            scale={scale}
            setScale={setScale}
          />
        )}

        <Box h="100%" overflowY="auto" ref={pdfContainerRef}>
          <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
                renderMode="canvas" // Ensure rendering as canvas for higher quality
              />
            ))}
          </Document>
        </Box>
      </Flex>
    </Box>
  );
};

interface PDFReaderBrowserProps {
  file: string;
  content_type: string;
  preview_url: string;
  containerStyles?: BoxProps;
  showControls?: boolean;
  showReplaceControl?: boolean;
  hideControl?: boolean;
  isFullscreen?: boolean;
  handleUpload?: (selectedFiles: File[]) => void;
  acceptedFiles?: string[];
  maxFiles?: number;
  styles?: Record<string, any>;
  fileName?: string;
  updateFromResumeControl?: React.ReactNode;
}

export const PDFReaderBrowser: React.FC<PDFReaderBrowserProps> = ({
  file,
  content_type,
  containerStyles = {},
  showControls = true,
  showReplaceControl = false,
  preview_url = null,
  isFullscreen = false,
  handleUpload = () => {},
  acceptedFiles = [],
  maxFiles = 1,
  styles = {},
  fileName = 'document',
  updateFromResumeControl,
}) => {
  const toast = useShowToast();
  const [pdfError, setPdfError] = useState(false);
  const [unsupportedFormat, setUnsupportedFormat] = useState(false);
  const [isPdfLoaded, setIsPdfLoaded] = useState(false);
  const isPdfFile = content_type === 'application/pdf';
  const attachFileRef = useRef(null);
  const isPreviewAvailable =
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/x-ole-storage',
    ]?.includes(content_type) && preview_url;
  const fileUrl = isPreviewAvailable ? preview_url : file;

  const { feNewPdfViewer } = useKFlags();

  const fullscreenDisclosure = useDisclosure();

  useEffect(() => {
    const checkFileFormat = () => {
      if (!isPdfFile && !isPreviewAvailable) {
        setUnsupportedFormat(true);
        return;
      }
    };

    const checkPdfAvailability = async () => {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('PDF not available');
        }
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfError(true);
      }
    };

    checkFileFormat();
    if (!unsupportedFormat) {
      checkPdfAvailability();
    }
  }, [unsupportedFormat, fileUrl, isPdfFile, isPreviewAvailable]);

  const handleDownload = () => {
    downloadFileFromUrl({
      url: fileUrl,
      filename: `${fileName}.pdf`,
      onSuccess: () => {
        toast({ title: `File downloaded` });
      },
      onError: (error) => {
        toast({ title: 'Failed to download', status: 'error' });
      },
    });
  };

  if (pdfError) {
    return (
      <Box w="100%" h="100%" p={'24px'} {...containerStyles}>
        <KText>Unable to load PDF. Try downloading it instead.</KText>
        <IconButton
          text="Download"
          icon={Download01}
          iconContainerProps={{
            width: 'unset',
            height: 'unset',
            bg: 'unset',
          }}
          iconProps={{ width: '14px', height: '14px' }}
          gap="4px"
          onClick={handleDownload}
        />
      </Box>
    );
  }

  if (unsupportedFormat) {
    return (
      <Box {...containerStyles}>
        <KText fontSize="sm">Resume preview unavailable for this format</KText>
        <IconButton
          mt="14px"
          text="Download"
          icon={Download01}
          iconContainerProps={{
            width: 'unset',
            height: 'unset',
            bg: 'unset',
          }}
          iconProps={{ width: '14px', height: '14px' }}
          gap="4px"
          onClick={handleDownload}
        />
      </Box>
    );
  }

  if (feNewPdfViewer) {
    return (
      <PdfViewer
        url={fileUrl}
        styles={styles}
        hideToolbar={!showControls}
        handleUpload={handleUpload}
        showReplaceControl={showReplaceControl}
        filename={fileName}
        updateFromResumeControl={
          showControls ? updateFromResumeControl : undefined
        }
      />
    );
  }

  const handleIframeLoad = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const iframe = event.currentTarget;

    // Confirm the iframe content has been loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      setIsPdfLoaded(true);
    } else {
      setTimeout(() => setIsPdfLoaded(true), 100); // Fallback for delayed state
    }
  };

  const iframeSource = showControls
    ? fileUrl
    : `${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`;

  const renderFullscreen = () => {
    return (
      <Flex
        position="absolute"
        top="13px"
        right="124px"
        onClick={fullscreenDisclosure.onOpen}
      >
        <Flex
          justify="center"
          align="center"
          w="28px"
          h="28px"
          borderRadius="full"
          transition="background-color 0.2s"
          cursor="pointer"
          _hover={{
            bg: 'rgb(66 70 73)',
          }}
          title="Fullscreen"
        >
          <Icon as={Maximize01} w="15px" h="15px" color="white" />
        </Flex>
      </Flex>
    );
  };

  const renderReplace = () => {
    return (
      <Flex
        cursor="pointer"
        position="absolute"
        top="13px"
        right="154px"
        onClick={() => attachFileRef?.current?.click()}
      >
        <Flex
          cursor="pointer"
          justify="center"
          align="center"
          gap="6px"
          p="4px 8px"
          borderRadius="xs"
          transition="background-color 0.2s"
          _hover={{
            bg: 'rgb(66 70 73)',
          }}
          title="Replace resume"
        >
          <input
            ref={attachFileRef}
            type="file"
            accept={acceptedFiles.join(', ')}
            onChange={(event) => {
              if (event?.target?.files?.length) {
                handleUpload(Array.from(event?.target?.files));
              }
            }}
            max={maxFiles}
            style={{ visibility: 'hidden', height: 0, width: 0 }}
          />
          <Icon as={Replace} w="15px" h="15px" color="white" />
          <KText size="sm-regular" color="white">
            Replace
          </KText>
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <Box
        w="100%"
        h="100%"
        overflow="hidden"
        position="relative"
        {...containerStyles}
      >
        {!isFullscreen && showControls && isPdfLoaded && (
          <>
            {showReplaceControl && renderReplace()}
            {renderFullscreen()}
            {updateFromResumeControl && (
              <Box position="absolute" top="8px" right="170px" zIndex={10}>
                {updateFromResumeControl}
              </Box>
            )}
          </>
        )}
        <iframe
          src={iframeSource}
          width="100%"
          height="100%"
          style={{ border: 'none', background: 'white' }}
          title="PDF Viewer"
          onLoad={handleIframeLoad}
        >
          <p>
            Your browser doesn't support PDF viewing.
            <Link href={fileUrl} download>
              Click here to download the PDF file.
            </Link>
          </p>
        </iframe>
      </Box>
      {fullscreenDisclosure.isOpen && (
        <FullScreenModal
          isOpen={fullscreenDisclosure.isOpen}
          onClose={fullscreenDisclosure.onClose}
        >
          <Box h="100%" w="100%">
            <PDFReaderBrowser
              file={file}
              content_type={content_type}
              containerStyles={containerStyles}
              showControls={showControls}
              preview_url={preview_url}
              fileName={fileName}
              isFullscreen
            />
          </Box>
        </FullScreenModal>
      )}
    </>
  );
};
